import * as React from 'react';

import {convertToArray, safelyGetFilterByKey} from 'nutshell-core/filter';
import type {FilterObject} from 'nutshell-core/types';

import {StackLegacy} from 'shells/layout/stack/stack-legacy';

import {analyticsClient} from '../../../analytics-client';

import {getReportDateRanges} from '../../reports/report-constants';
import {ReportDateRangePickerSimple} from '../../reports/report-date-range-picker-simple';
import {ENGAGEMENT_DASHBOARD} from '../../reports/report-types';
import type {ReportURLParams} from '../../reports/report-types';

import {useGetFacebookIntegration} from '../facebook/graphql/hooks';
import {useGetWhatsAppIntegration} from '../whatsapp/graphql/hooks';

import {ChatCard} from './chat-card';
import {ChatbotCard} from './chatbot-card';
import {Checklist} from './checklist';
import {FacebookCard} from './facebook-card';
// @ts-expect-error
import {useGetInboxDashboardChecklist} from './graphql/hooks';
import {hasEmbeddedChat, hasConfiguredSMS} from './helpers';
import {InstagramCard} from './instagram-card';
import {SMSCard} from './sms-card';
import {WhatsAppCard} from './whatsapp-card';

export const DEFAULT_DATE_RANGE = '-d30';

type Props = {
    activeFilters: FilterObject[];
    onParamChange: (arg1: any, arg2: boolean) => any;
    urlParams: ReportURLParams<'contacts'>;
};

export function Dashboard(props: Props) {
    const {inboxDashboardChecklist, checklistIsComplete, completedCount, totalCount} =
        useGetInboxDashboardChecklist();
    const {facebookIntegration, isLoading: isFacebookIntegrationLoading} =
        useGetFacebookIntegration();
    const {whatsAppIntegration, isLoading: isWhatsAppIntegrationLoading} =
        useGetWhatsAppIntegration();

    const dateRangeType = 'period';
    const dateRangeFilter = safelyGetFilterByKey(props.activeFilters, dateRangeType);

    const dateRange = dateRangeFilter || DEFAULT_DATE_RANGE;

    const shouldShowChatCardPromo = inboxDashboardChecklist
        ? !hasEmbeddedChat(inboxDashboardChecklist.items)
        : false;
    const shouldShowSMSCardPromo = inboxDashboardChecklist
        ? !hasConfiguredSMS(inboxDashboardChecklist.items)
        : false;
    const shouldShowFacebookCardPromo =
        !isFacebookIntegrationLoading &&
        (!facebookIntegration || !facebookIntegration.hasAuthToken);

    const shouldShowWhatsAppCardPromo =
        !isWhatsAppIntegrationLoading &&
        (!whatsAppIntegration || !whatsAppIntegration.hasAuthToken);

    // @ts-expect-error
    const handleDateRangeChange = (newFilter) => {
        props.onParamChange({filter: {...props.urlParams.filter, ...newFilter}}, true);

        analyticsClient.track(analyticsClient.EVENTS.TRACK_ENGAGEMENT_DASHBOARD_DATE_RANGE_CHANGED);
    };

    React.useEffect(() => {
        analyticsClient.track(analyticsClient.EVENTS.TRACK_ENGAGEMENT_DASHBOARD_VIEWED, {
            dateRange: dateRange,
        });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    return (
        <div className='pt-16 px-16 flex flex-dir-col border-box full-height full-width'>
            <div className='flex justify-end mb-16'>
                <ReportDateRangePickerSimple
                    activeFilters={convertToArray(props.urlParams.filter)}
                    dateRangeType={dateRangeType}
                    // @ts-expect-error
                    dateRangeOptions={getReportDateRanges(ENGAGEMENT_DASHBOARD)}
                    onDateRangeChange={handleDateRangeChange}
                />
            </div>
            <div className='overflow-hidden'>
                <div className='full-height overflow-auto'>
                    <div className='mb-32'>
                        <StackLegacy spacing={16}>
                            <Checklist
                                inboxDashboardChecklist={inboxDashboardChecklist}
                                checklistIsComplete={checklistIsComplete}
                                completedCount={completedCount}
                                totalCount={totalCount}
                                hasCollapsedButton={true}
                            />
                            <ChatCard
                                dateRange={dateRange}
                                activeFilters={props.activeFilters}
                                onParamChange={props.onParamChange}
                                shouldShowPromo={shouldShowChatCardPromo}
                            />
                            <ChatbotCard
                                dateRange={dateRange}
                                activeFilters={props.activeFilters}
                                onParamChange={props.onParamChange}
                                shouldShowPromo={shouldShowChatCardPromo}
                            />
                            <InstagramCard
                                dateRange={dateRange}
                                activeFilters={props.activeFilters}
                                onParamChange={props.onParamChange}
                                shouldShowPromo={shouldShowFacebookCardPromo}
                            />
                            <FacebookCard
                                dateRange={dateRange}
                                activeFilters={props.activeFilters}
                                onParamChange={props.onParamChange}
                                shouldShowPromo={shouldShowFacebookCardPromo}
                            />
                            <SMSCard
                                dateRange={dateRange}
                                activeFilters={props.activeFilters}
                                onParamChange={props.onParamChange}
                                shouldShowPromo={shouldShowSMSCardPromo}
                            />
                            <WhatsAppCard
                                dateRange={dateRange}
                                activeFilters={props.activeFilters}
                                onParamChange={props.onParamChange}
                                shouldShowPromo={shouldShowWhatsAppCardPromo}
                            />
                        </StackLegacy>
                    </div>
                </div>
            </div>
        </div>
    );
}
